import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, XCircle, CheckCircle, Clock } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

function fmtDate(s: string) {
  return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

interface Boost {
  id: number;
  storeId: number;
  storeName: string;
  towns: string[];
  durationHours: number;
  pricePence: number;
  startsAt: string;
  endsAt: string;
  cancelledAt: string | null;
  feeCollectedAt: string | null;
  notes: string | null;
  createdAt: string;
}

function boostStatus(b: Boost): "active" | "expired" | "cancelled" {
  if (b.cancelledAt) return "cancelled";
  if (new Date(b.endsAt) < new Date()) return "expired";
  return "active";
}

export default function AdminBoosts() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [boosts, setBoosts] = useState<Boost[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const res = await fetch("/api/admin/boosts", { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json() as { boosts: Boost[] };
      setBoosts(data.boosts);
    }
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  async function cancel(id: number) {
    if (!confirm("Cancel this boost early?")) return;
    const res = await fetch(`/api/admin/boosts/${id}/cancel`, { method: "PATCH", headers: authHeaders() });
    if (res.ok) {
      toast({ title: "Boost cancelled" });
      void load();
    } else {
      toast({ title: "Failed to cancel", variant: "destructive" });
    }
  }

  async function toggleFeeCollected(b: Boost) {
    const res = await fetch(`/api/admin/boosts/${b.id}/collect-fee`, { method: "PATCH", headers: authHeaders() });
    if (res.ok) {
      toast({ title: b.feeCollectedAt ? "Fee marked uncollected" : "Fee marked collected" });
      void load();
    }
  }

  if (isLoading) return null;
  if (!user || user.role !== "admin") return <Redirect to="/admin" />;

  const active = boosts.filter(b => boostStatus(b) === "active");
  const past = boosts.filter(b => boostStatus(b) !== "active");
  const totalOutstanding = boosts.filter(b => b.pricePence > 0 && !b.feeCollectedAt && !b.cancelledAt).reduce((s, b) => s + b.pricePence, 0);

  return (
    <AdminLayout current="/admin/boosts">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Zap size={22} className="text-amber-500" /> Store Boosts
            </h1>
            <p className="text-sm text-slate-500 mt-1">Admin-boosted stores appear at the top of listings for selected towns.</p>
          </div>
          {totalOutstanding > 0 && (
            <div className="text-right">
              <p className="text-xs text-slate-500">Outstanding fees</p>
              <p className="text-xl font-bold text-red-600">{pence(totalOutstanding)}</p>
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : (
          <>
            {active.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Active ({active.length})</h2>
                <div className="space-y-2">
                  {active.map(b => (
                    <div key={b.id} className="bg-white border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground">{b.storeName}</span>
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                            <Zap size={10} className="mr-1" /> Active
                          </Badge>
                          {b.pricePence > 0 && !b.feeCollectedAt && (
                            <Badge variant="outline" className="text-red-600 border-red-300 text-xs">Fee owed: {pence(b.pricePence)}</Badge>
                          )}
                          {b.pricePence > 0 && b.feeCollectedAt && (
                            <Badge variant="outline" className="text-green-600 border-green-300 text-xs"><CheckCircle size={10} className="mr-1" />{pence(b.pricePence)} collected</Badge>
                          )}
                          {b.pricePence === 0 && <Badge variant="outline" className="text-slate-500 text-xs">Free</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Towns: {b.towns.join(", ")} · {b.durationHours}h · Ends {fmtDate(b.endsAt)}
                        </p>
                        {b.notes && <p className="text-xs text-slate-400 mt-0.5">{b.notes}</p>}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {b.pricePence > 0 && (
                          <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => void toggleFeeCollected(b)}>
                            {b.feeCollectedAt ? "Unmark collected" : "Mark fee collected"}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => void cancel(b.id)}>
                          <XCircle size={12} className="mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {past.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">History ({past.length})</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b">
                        <th className="pb-2 pr-4 font-medium">Store</th>
                        <th className="pb-2 pr-4 font-medium">Towns</th>
                        <th className="pb-2 pr-4 font-medium">Duration</th>
                        <th className="pb-2 pr-4 font-medium">Price</th>
                        <th className="pb-2 pr-4 font-medium">Status</th>
                        <th className="pb-2 pr-4 font-medium">Fee</th>
                        <th className="pb-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {past.map(b => {
                        const status = boostStatus(b);
                        return (
                          <tr key={b.id} className="text-slate-600">
                            <td className="py-2 pr-4 font-medium text-foreground">{b.storeName}</td>
                            <td className="py-2 pr-4 text-xs">{b.towns.join(", ")}</td>
                            <td className="py-2 pr-4">{b.durationHours}h</td>
                            <td className="py-2 pr-4">{b.pricePence === 0 ? "Free" : pence(b.pricePence)}</td>
                            <td className="py-2 pr-4">
                              {status === "cancelled"
                                ? <span className="text-red-500 flex items-center gap-1"><XCircle size={12} />Cancelled</span>
                                : <span className="text-slate-400 flex items-center gap-1"><Clock size={12} />Expired</span>
                              }
                            </td>
                            <td className="py-2 pr-4">
                              {b.pricePence === 0 ? "—" : b.feeCollectedAt
                                ? <span className="text-green-600 flex items-center gap-1"><CheckCircle size={12} />Collected</span>
                                : <span className="text-red-500">Uncollected</span>
                              }
                            </td>
                            <td className="py-2 text-xs text-slate-400">{fmtDate(b.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {boosts.length === 0 && (
              <div className="text-center py-16 text-slate-400">
                <Zap size={40} className="mx-auto mb-3 opacity-30" />
                <p>No boosts yet. Use the Boost button on any store in the Shops page.</p>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
